import styled, { css } from 'styled-components';

import { Colors, ScrollBarStyle, Shadows } from 'environment';
import { getTypographyCSS, FontWeights } from 'components/UI/Typography';
import { Disabled } from 'helpers/cssGenerators';

export const Container = styled.div`
	width: 100%;
	position: relative;
`;

export const SearchValueContainer = styled.div`
	display: flex;
	align-items: center;
	overflow: hidden;
`;

interface SelectContainerProps extends Disabled {
	error?: boolean;
	hasValue?: boolean;
	allowCreate?: boolean;
	disabled?: boolean;
	readOnly?: boolean;
	width?: number;
	dropdownWidth?: number;
}
export const SelectContainer = styled.div<SelectContainerProps>`
	width: ${({ width }) => (width ? width + 'rem' : '100%')};
	background-color: ${Colors.white};

	.select-container {
		/* TYPOGRAPHY SETTER */
		${getTypographyCSS(t => t.Paragraph)}

		min-height: 4rem;
	}

	.select__option--is-focused {
		background-color: unset;
	}

	.select__option--is-disabled {
		cursor: default !important;
		color: ${Colors.text.disabled} !important;
	}

	.select__menu {
		width: ${({ dropdownWidth }) => (dropdownWidth ? dropdownWidth + 'rem' : '100%')};
		margin-top: 0.4rem;
		box-shadow: ${Shadows.hover};
	}

	.select__menu-list {
		padding: 0.8rem 0;
		overflow-y: auto;
		overflow-y: overlay;

		scrollbar-width: thin;
		scrollbar-color: #748394 transparent;

		> :first-child {
			margin-top: 0;
		}
		> :last-child {
			margin-bottom: 0;
		}

		${ScrollBarStyle}
	}

	.select__control {
		box-shadow: unset;
		min-height: 4rem;
		border: 0.12rem solid ${({ error }) => (error ? Colors.text.error : Colors.text.disabled)};
		background-color: ${Colors.white};
		cursor: ${({ allowCreate }) => (allowCreate ? 'text' : 'pointer')};
	}

	.select__dropdown-indicator {
		padding: 0;
		margin-right: 0.8rem;
		color: ${Colors.text.main};
		transition: transform 0.25s;

		:hover {
			color: ${Colors.text.main};
		}

		svg {
			:hover {
				color: ${Colors.text.main};
			}
		}
	}

	.select__clear-indicator {
		padding: 0;
		margin-right: 0.8rem;
		color: ${Colors.text.disabled};
		transition: transform 0.25s;

		:hover {
			color: ${Colors.text.main};
		}

		svg {
			height: 1.6rem;
			width: 1.6rem;

			:hover {
				color: ${Colors.text.main};
			}
		}
	}

	.select__indicators,
	.select__menu,
	.select__menu-list,
	.select__option,
	.select__multi-value__remove {
		cursor: pointer;
	}

	.select__multi-value__remove:hover {
		background-color: unset;
		color: unset;
	}

	.select__option {
		/* enable next 3 lines if we want options to be displayed on a single line */
		/* white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis; */
		padding: 0.4rem 0.8rem;

		:active {
			background: ${Colors.primary.normal};
			color: ${Colors.white};
		}
	}

	.select__option--is-selected {
		background: ${Colors.white};
		color: ${Colors.primary.normal};
	}

	.select__option {
		:hover {
			background: ${Colors.primary.normal};
			color: ${Colors.white};
		}
	}

	.select__control--is-focused {
		border: 0.12rem solid ${Colors.primary.normal};

		.select__dropdown-indicator {
			transform: rotate(180deg);
		}

		:hover {
			border: 0.12rem solid ${Colors.primary.normal};
		}
	}

	.select__placeholder {
		color: ${Colors.text.caption};
	}

	.select__multi-value {
		border-radius: 0.4rem;
		color: ${Colors.primary.normal};
		background-color: ${Colors.primary.disabled};

		.select__multi-value__label {
			${getTypographyCSS(t => t.Caption)}
			color: ${Colors.primary.normal};
			font-weight: ${FontWeights.medium};
			padding: 0.1rem 0;
			padding-left: 0.8rem;
		}
	}

	.select__indicator-separator {
		display: none;
	}

	.select__group {
		padding: 0;
		margin: 0.8rem 0;

		.select__group-heading {
			${getTypographyCSS(t => t.Hint)}

			margin: 0;
			padding: 0 0.8rem;
			color: ${Colors.text.disabled};
		}
	}

	${({ readOnly }) =>
		readOnly &&
		css`
			pointer-events: none;

			.select__control {
				border-color: ${Colors.text.disabled};
				background-color: ${Colors.background.disabled};
			}

			.select__placeholder {
				color: ${Colors.text.disabled};
			}

			.select__clear-indicator {
				display: none;
			}
		`};

	${({ disabled }) =>
		disabled &&
		css`
			pointer-events: none;

			.select__control {
				border-color: ${Colors.background.disabled};
				background-color: ${Colors.background.disabled};
			}

			.select__placeholder,
			.select__value-container,
			.select__single-value {
				color: ${Colors.text.disabled};
			}

			.select__indicators {
				display: none;
			}
		`};

	${({ error }) =>
		error &&
		css`
			.select__control--is-focused,
			.select__control--is-focused:hover,
			.select__control:hover,
			.select__control:active {
				border: 0.12rem solid ${Colors.text.error};
			}
		`}
`;
